import React, { useState, useEffect } from "react";
import { Container, Typography, Box, Button, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { pb, getCurrentUser, hasAdminPermission } from "../services/pocketbase";
import ConductRuleEditor from "../components/ConductRuleEditor";
// eslint-disable-next-line
import ReactMarkdown from "react-markdown";

// Discord-style markdown formatting function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const CodeOfConduct = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const user = getCurrentUser();
  const isAdmin = hasAdminPermission(user);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);

      const records = await pb.collection("conduct_rules").getList(1, 100, {
        sort: "rule_number",
      });

      setRules(records.items);
    } catch (err) {
      console.error("Error fetching rules:", err);
      setError("Failed to load rules. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleAddRule = () => {
    setSelectedRule(null);
    setEditorOpen(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setEditorOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) {
      return;
    }

    try {
      // Get the rule data before deleting it
      const ruleToDelete = rules.find(rule => rule.id === ruleId);
      
      // Delete the rule
      await pb.collection("conduct_rules").delete(ruleId);
      
      // Send webhook for rule deletion
      try {
        await fetch('/api/webhooks/coc-logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            embeds: [{
              title: "Code of Conduct Rule Deleted",
              color: 0xff0000, // Red color
              fields: [
                {
                  name: "Deleted By",
                  value: user?.username || "Unknown User",
                  inline: true
                },
                {
                  name: "Timestamp",
                  value: new Date().toISOString(),
                  inline: true
                },
                {
                  name: "Deleted Rule",
                  value: `**Rule ${ruleToDelete.rule_number}${ruleToDelete.rule_title ? `: ${ruleToDelete.rule_title}` : ''}**\n${ruleToDelete.rule_content}`,
                }
              ]
            }]
          })
        });
      } catch (webhookError) {
        console.error("Failed to send webhook for rule deletion:", webhookError);
      }
      
      await fetchRules();
    } catch (err) {
      console.error("Error deleting rule:", err);
      setError("Failed to delete rule. Please try again.");
    }
  };

  const handleEditorClose = (refresh) => {
    setEditorOpen(false);
    if (refresh) {
      fetchRules();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          CODE OF CONDUCT
        </Typography>

        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRule}
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            ADD RULE
          </Button>
        )}
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography>Loading Code of Conduct...</Typography>
      ) : (
        <Box
          sx={{
            backgroundColor: "rgba(40, 44, 52, 0.99)",
            borderRadius: 2,
          }}
        >
          {rules.map((rule) => (
            <Box
              key={rule.id}
              sx={{
                position: "relative",
                padding: "20px",
              }}
            >
              <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                {rule.rule_number}. {rule.rule_title}
              </Typography>

              {/* Updated to use Discord markdown formatting */}
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  "& code": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    padding: "2px 4px",
                    borderRadius: "3px",
                    fontFamily: "monospace",
                  },
                  "& pre": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    padding: "8px",
                    borderRadius: "4px",
                    overflowX: "auto",
                  },
                  "& strong": {
                    fontWeight: "bold",
                  },
                  "& em": {
                    fontStyle: "italic",
                  },
                  "& u": {
                    textDecoration: "underline",
                  },
                  "& s": {
                    textDecoration: "line-through",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: formatDiscordMarkdown(rule.rule_content),
                }}
              />

              {/* Position icons absolutely in the top right corner */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {isAdmin && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleEditRule(rule)}
                      sx={{ color: "primary.main" }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRule(rule.id)}
                      sx={{ color: "error.main" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <ConductRuleEditor
        open={editorOpen}
        onClose={handleEditorClose}
        rule={selectedRule}
      />
    </Container>
  );
};

export default CodeOfConduct;
